import React, { useEffect, useMemo, useRef, useState } from 'react'

import { ResponsiveContainer, BarChart, Bar, CartesianGrid, Cell } from 'recharts'
import { motion } from 'framer-motion'
import Confetti from 'react-confetti'
import { GetActiveStadiumZones } from './../../../utilities/BL/Zones'

/**
 * Payload esperado (ejemplo):
 * {
 *   data: [
 *     { QuestionId: 7, CalculatedPointsByZone: { canels: 1, tecate: 1, tequis: 1 } },
 *     { QuestionId: 6, CalculatedPointsByZone: { canels: 1, tequis: 1 } },
 *     ...
 *   ],
 *   idTile: 3.1,
 *   isActiveWomensMode: false,
 *   type: "finalResults"
 * }
 */

/* =========================
 * 1) Constantes y mapeos
 * ========================= */

const MODES = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
}



// Utilidad: normaliza a slug comparable (lowercase sin tildes básicas)
const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

/* =========================
 * 2) Utilidades puras
 * ========================= */

/**
 * Retorna arreglo de zonas del modo activo.
 */
function getModeZones(isActiveWomensMode, activeZonesList) {
  // Retorna todas las zonas configuradas para la sala activa
  return activeZonesList;
}

/**
 * Extrae las preguntas y respuestas del payload independientemente de si
 * viene como un Arreglo nativo o como un Objeto asociativo con llaves numéricas.
 */
function extractEntries(payload) {
  let rawData = payload?.data;
  
  if (!rawData) return [];
  
  if (Array.isArray(rawData)) {
    return rawData;
  }
  
  if (typeof rawData === 'object') {
    return Object.keys(rawData)
      .filter((key) => !isNaN(key))
      .map((key) => rawData[key]);
  }
  
  return [];
}

/**
 * Agrega puntos por zona (del modo activo) a partir del payload.
 * Retorna un mapa: { slug: totalPoints }
 */
function aggregatePointsByZone(payload, isActiveWomensMode, activeZonesList) {
  console.log('[Results.js] aggregatePointsByZone called. activeZonesList:', activeZonesList);
  const modeZones = getModeZones(isActiveWomensMode, activeZonesList)
  const allowedSlugs = new Set(modeZones.map((z) => normalize(z.slug)))
  console.log('[Results.js] allowedSlugs (normalized):', Array.from(allowedSlugs));
  const result = {}
  for (const slug of allowedSlugs) result[slug] = 0

  const entries = extractEntries(payload);

  console.log('[Results.js] Found payload entries count:', entries.length);

  for (const item of entries) {
    const calc = item?.CalculatedPointsByZone || item?.calculatedPointsByZone

    if (!calc || typeof calc !== 'object') continue

    for (const [zoneKey, pts] of Object.entries(calc)) {
      const slug = normalize(zoneKey)
      console.log(`[Results.js] Processing entry point: key="${zoneKey}" (normalized="${slug}") = ${pts} pts`);
      if (allowedSlugs.has(slug)) {
        const val = Number(pts) || 0
        result[slug] = (result[slug] ?? 0) + val
      } else {
        console.warn(`[Results.js] Normalization MISMATCH or zone not configured: "${slug}" was not found in allowedSlugs!`);
      }
    }
  }
  console.log('[Results.js] Aggregated pointsMap:', result);
  return result
}

/**
 * Determina si NO hubo puntos calculados en ninguna pregunta del payload.
 * (i.e., todas las preguntas carecen de CalculatedPointsByZone o vienen vacías)
 */
function noCalculatedPointsAtAll(payload) {
  const entries = extractEntries(payload);
  
  if (entries.length === 0) return true

  const empty = entries.every((item) => {
    const c = item?.CalculatedPointsByZone || item?.calculatedPointsByZone
    return !c || Object.keys(c).length === 0
  })
  console.log('[Results.js] noCalculatedPointsAtAll evaluation:', empty);
  return empty
}

/**
 * Construye datos para la gráfica a partir del mapa { slug: points }.
 */
function buildChartData(pointsMap, isActiveWomensMode, activeZonesList) {
  const modeZones = getModeZones(isActiveWomensMode, activeZonesList)
  const chartData = modeZones.map((z) => ({
    slug: z.slug,
    name: z.label, // lo que verá el usuario en el eje X
    value: pointsMap[normalize(z.slug)] ?? 0,
  }))
  console.log('[Results.js] buildChartData output:', chartData);
  return chartData
}

/**
 * Genera datos aleatorios para animación (mismo shape que buildChartData).
 * El rango de valores se puede ajustar. Se usa un rango pequeño para “suspenso”.
 */
function randomChartData(isActiveWomensMode, activeZonesList, max = 20) {
  const modeZones = getModeZones(isActiveWomensMode, activeZonesList)
  return modeZones.map((z) => ({
    slug: z.slug,
    name: z.label,
    value: Math.floor(Math.random() * max) + 1,
  }))
}

// finalData: [{ slug, name, value }]
// totalZones: cantidad de zonas del modo activo (finalChartData.length)
/**
 * Calcula zonas ganadoras a partir de datos definitivos [{slug,name,value}].
 * Reglas especiales:
 *  - Si max = 0 → pick aleatoria (una sola ganadora).
 */
const computeWinners = (finalData, totalZones) => {
  console.log('[Results.js] computeWinners called. finalData:', finalData, 'totalZones:', totalZones);
  if (!finalData?.length) {
    return { winners: [], allEqualPositive: false, pickedRandom: null, allZonesTied: false }
  }

  const values = finalData.map((d) => Number(d.value) || 0)
  const max = Math.max(...values)
  console.log('[Results.js] computeWinners: points values:', values, 'max:', max);

  // Todos en 0 → elegir una al azar
  if (max === 0) {
    const randomIdx = Math.floor(Math.random() * finalData.length)
    const picked = finalData[randomIdx]
    console.log('[Results.js] computeWinners: max is 0, picked random winner:', picked);
    return {
      winners: [picked],
      allEqualPositive: false,
      pickedRandom: picked,
      allZonesTied: false,
    }
  }

  const winners = finalData.filter((d) => Number(d.value) === max)
  const allZonesTied = winners.length === totalZones && max > 0
  console.log('[Results.js] computeWinners output: winners:', winners, 'allZonesTied:', allZonesTied);

  return { winners, allEqualPositive: allZonesTied, pickedRandom: null, allZonesTied }
}

/* =========================
 * 3) Hook de estado/flujo
 * ========================= */

function useResultsFlow(payload) {
  console.log('[Results.js] useResultsFlow hook called with payload:', payload);
  // Fases: 'animating' → 'reveal' → 'winner'
  const [phase, setPhase] = useState('animating')
  const [animatedData, setAnimatedData] = useState([])
  const [finalData, setFinalData] = useState([])
  const [winners, setWinners] = useState([])
  const [allEqualPositive, setAllEqualPositive] = useState(false)
  const [pickedRandom, setPickedRandom] = useState(null)
  const intervalRef = useRef(null)
  const revealTimeoutRef = useRef(null)
  const winnerTimeoutRef = useRef(null)
  const [allZonesTied, setAllZonesTied] = useState(false)
  const [stadiumZones, setStadiumZones] = useState([])

  const isActiveWomensMode = !!payload?.isActiveWomensMode

  useEffect(() => {
    const msgRoomId = payload?.roomId || payload?.RoomId;
    console.log('[Results.js] useEffect: payload changed. msgRoomId:', msgRoomId);
    GetActiveStadiumZones(msgRoomId).then((res) => {
      console.log('[Results.js] GetActiveStadiumZones response:', res);
      if (res.success && res.data.length > 0) {
        setStadiumZones(res.data)
      } else {
        console.warn('[Results.js] GetActiveStadiumZones empty or error response:', res);
      }
    })
  }, [payload])

  const activeZones = useMemo(() => {
    if (stadiumZones.length > 0) {
      const mapped = stadiumZones.map((z) => ({
        slug: z.internalName,
        label: z.displayName,
        internal: z.displayName,
        color: z.color || null,
        mode: z.internalName.startsWith('zona') ? MODES.MALE : (payload?.isActiveWomensMode ? MODES.FEMALE : MODES.MALE)
      }))
      console.log('[Results.js] activeZones mapped output:', mapped);
      return mapped;
    }
    return []
  }, [stadiumZones, payload?.isActiveWomensMode])

  // Cálculos estáticos derivados del payload
  const pointsMap = useMemo(() => aggregatePointsByZone(payload, isActiveWomensMode, activeZones), [payload, isActiveWomensMode, activeZones])
  const finalChartData = useMemo(() => buildChartData(pointsMap, isActiveWomensMode, activeZones), [pointsMap, isActiveWomensMode, activeZones])
  const noPointsAtAll = useMemo(() => noCalculatedPointsAtAll(payload), [payload])

  const totalZones = finalChartData.length

  useEffect(() => {
    console.log('[Results.js] Animation useEffect ran. stadiumZones count:', stadiumZones.length, 'finalChartData:', finalChartData);
    // Esperar a que se carguen las zonas activas de la sala
    if (stadiumZones.length === 0) {
      console.log('[Results.js] Animation useEffect: stadiumZones is empty, returning.');
      return;
    }

    // Si no hay zonas para el modo, salimos
    if (finalChartData.length === 0) {
      console.warn('[Results.js] Animation useEffect: finalChartData has no elements.');
      setPhase('winner')
      setAnimatedData([])
      setFinalData([])
      setWinners([])
      setAllEqualPositive(false)
      setPickedRandom(null)
      return
    }

    // 1) Empezar animación (valores aleatorios)
    setPhase('animating')
    setAnimatedData(randomChartData(isActiveWomensMode, activeZones))
    intervalRef.current = setInterval(() => {
      setAnimatedData(randomChartData(isActiveWomensMode, activeZones))
    }, 120)

    // 2) A los ~3s, revelar valores reales
    revealTimeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current)
      setAnimatedData(finalChartData)
      setFinalData(finalChartData)
      setPhase('reveal')

      // 3) Breve pausa y luego calcular ganadores
      winnerTimeoutRef.current = setTimeout(() => {
        // Regla especial: si no hubo puntos calculados en ninguna pregunta → “Ganaron todas”
        if (noPointsAtAll) {
          console.log('[Results.js] Animation useEffect: noPointsAtAll evaluated to true.');
          setWinners(finalChartData) // todas ganan
          setAllEqualPositive(true)  // para el mensaje “¡Ganaron todas…!”
          setAllZonesTied(true)
          setPickedRandom(null)
          setPhase('winner')
          return
        }

        const { winners, allEqualPositive, pickedRandom, allZonesTied } = computeWinners(finalChartData, totalZones)

        setWinners(winners)
        setAllEqualPositive(allEqualPositive)
        setPickedRandom(pickedRandom)
        setAllZonesTied(allZonesTied)
        setPhase('winner')
      }, 500)
    }, 3000)

    // Limpieza al desmontar o al cambiar el payload
    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(revealTimeoutRef.current)
      clearTimeout(winnerTimeoutRef.current)
    }
  }, [payload, isActiveWomensMode, finalChartData, noPointsAtAll, activeZones, totalZones, stadiumZones])

  return { phase, animatedData, finalData, winners, allEqualPositive, pickedRandom, isActiveWomensMode, allZonesTied, activeZones }
}

/* =========================
 * 4) Componente principal
 * ========================= */

const Results = ({ payload }) => {
  console.log('[Results.js] RAW payload received inside Results component:', payload);
  const { phase, animatedData, winners, pickedRandom, allZonesTied, isActiveWomensMode, activeZones } = useResultsFlow(payload)

  const showChart = phase === 'animating' || phase === 'reveal'
  const showWinner = phase === 'winner' && winners.length > 0

  // Tamaño seguro para Confetti
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024
  const height = typeof window !== 'undefined' ? window.innerHeight : 768

  useEffect(() => {
    if (showChart) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    }
  }, [showChart])

  // Paletas por modo (elige tonos buenos en negro)
  const PALETTE_MALE = {
    'zona1': '#4CC9F0',
    'zona2': '#F72585',
    'zona3': '#B5179E',
    'zona4': '#F94144',
    'zona5': '#F3722C',
    'zona6': '#90BE6D',
    'zona7': '#F9C74F',
    'zona8': '#43AA8B',

    'laboratorio tequis': '#4CC9F0',
    canels: '#F72585',
    'seguros el potosí': '#B5179E',
    'red cola': '#F94144',
    tecate: '#F3722C',
    moctezuma: '#90BE6D',
    nissan: '#F9C74F',
    mobil: '#43AA8B',
    'lounge ron potosí': '#7f43aaff'
  }

  const PALETTE_FEMALE = {
    azul: '#60A5FA', // azul
    oro: '#F59E0B'   // oro
  }

  function getZoneColor(slug, isActiveWomensMode) {
    const key = normalize(slug)
    const foundZone = activeZones.find(z => normalize(z.slug) === key);
    if (foundZone && foundZone.color) {
      return foundZone.color;
    }
    const palette = isActiveWomensMode ? PALETTE_FEMALE : PALETTE_MALE
    return palette[key] || '#8B5CF6' // fallback
  }





  return (
    <div className="Results__FullScreenOverlay">
      {/* 📊 Gráfica (animación → revelado) */}
      {/* {showChart && animatedData.length > 0 && (
        <div style={{ minWidth: 0, minHeight: 0 /* altura controlada por aspect / }}>
          <ResponsiveContainer width="100%" height="100%" aspect={2}>
            <BarChart data={animatedData}>
              <CartesianGrid stroke="#3a3a3a" /* grid tenue / strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#eaeaea' }}
                axisLine={{ stroke: '#888' }}
                tickLine={{ stroke: '#888' }}
              />
              <YAxis
                tick={{ fill: '#eaeaea' }}
                axisLine={{ stroke: '#888' }}
                tickLine={{ stroke: '#888' }}
              />
              <Tooltip
                contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }}
                labelStyle={{ color: '#bbb' }}
                itemStyle={{ color: '#fff' }}
                formatter={(value) => [`${value} pts`, 'Zona']}
              />
              {/* Una barra con color por zona /}
              <Bar
                dataKey="value"
                isAnimationActive={true}
                animationDuration={400}
                // color dinámica por zona:
                fillOpacity={0.9}
              >
                {
                  // Si quieres colorear cada barra según el slug:
                  animatedData.map((entry, idx) => (
                    <cell
                      // en JSX real: usa <Cell />, aquí el tag es con mayúscula
                      /* eslint-disable jsx-a11y/aria-role /
                      key={`cell-${idx}`}
                      fill={getZoneColor(entry.slug, isActiveWomensMode)}
                    />
                  ))
                }
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )} */}

      {/* 📊 Gráfica (animación → revelado) */}
      {showChart && animatedData.length > 0 && (
        // <div className="w-full h-96">
        <div style={{ minWidth: 0, minHeight: 0, height: '24rem' }}>
          <ResponsiveContainer width="100%" height="100%" aspect={4}>
            <BarChart data={animatedData}>
              <CartesianGrid stroke="#3a3a3a" /* grid tenue */ strokeDasharray="3 3" />
              {/* <XAxis dataKey="name" tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} /> */}
              {/* <YAxis tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} /> */}
              {/* <Tooltip contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }} labelStyle={{ color: '#bbb' }} itemStyle={{ color: '#fff' }} formatter={(value) => [`${value} pts`, 'Zona']} /> */}
              {/* <Bar dataKey="value" fill="#60A5FA" /> */}
              <Bar dataKey="value" isAnimationActive animationDuration={400} fillOpacity={0.95}>
                {animatedData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={getZoneColor(entry.slug, isActiveWomensMode)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 🏆 Resultado */}
      {showWinner && (
        <>
          <Confetti width={width} height={height} recycle={false} numberOfPieces={300} />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1.12 }}
            transition={{ duration: 0.6, type: 'spring' }}
            className="text-4xl font-bold text-center mt-6"
          >
            <p><i className="bi bi-trophy fs-1"></i></p>

            {/* Mensajería de ganadores */}
            {(() => {
              const isExplicitTotalTie = payload?.isTotalTie === true || payload?.IsTotalTie === true;
              if (allZonesTied || isExplicitTotalTie) {
                return <p className="Results__FullScreenOverlay__LargeWinner">Ha habido un empate total.</p>
              }

              // Cuando ninguno respondió, se elegía uno al azar, pero lo cambié para que muestre un empate
              if (pickedRandom) {
                return <p className="Results__FullScreenOverlay__LargeWinner">Ha habido un empate total.</p>
              }

              if (winners) {
                if (winners.length === 0) {
                  return (
                    <p className="Results__FullScreenOverlay__OneWinner">Ha habido un empate total.</p>
                  )
                }
  
                if (winners.length === 1) {
                  return (
                    <p className="Results__FullScreenOverlay__OneWinner">
                      La zona ganadora es…<br />
                      {winners[0].name}
                    </p>
                  )
                }
  
                return (
                  <>
                    <p className="Results__FullScreenOverlay__MultipleWinner">¡Empate! Ganaron las zonas:</p>
                    {winners.map((w) => (
                      <p key={w.slug} className="Results__FullScreenOverlay__MultipleWinner">Zona {w.name}</p>
                    ))}
                  </>
                )
              } else {
                return (
                  <p className="Results__FullScreenOverlay__OneWinner">Ha habido un empate total.</p>
                )
              }
            })()}
          </motion.div>
        </>
      )}
    </div>
  )
}

export { Results };